import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  AddressModel,
  ControlProperty,
  FRACTIONAL_NUMBER_PATTERN,
  NATURAL_NUMBER_PATTERN,
  TruckSizeUnion,
  TruckStatus,
  UliActivityType,
  UliOrigin,
  UliStatus,
} from '../../../../../../common';
import { AllocationDurationType, DeviationType, LocalDate, LocalDateRangeModel } from '../../../../../models';
import { AllocationViewRuleModel } from '../../models';
import { UliItemTruck } from './uli-truck.model';

export enum UliItemStatusTypeEnum {
  UliStatusType = 'ULI_STATUS',
  TruckStatusType = 'TRUCK_STATUS',
}

export interface UliItemActionParams {
  uli: string;
  compartmentId: number;
  type: UliActivityType;
}

export class UliItemAllocation {
  compartmentName: string;
  dockName: string;
  ruleInfo: AllocationViewRuleModel;
  plannedSlot: LocalDateRangeModel;

  constructor(params?: UliItemUliInfo) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export interface UliItemOverviewStatuses {
  statuses: UliItemStatus[];
  truckStatusChains: UliItemTruckStatuses[];
}

export interface UliItemStatus {
  status: UliStatus | TruckStatus;
  createdBy: string;
  type: UliItemStatusTypeEnum;
  statusDate: {
    offset: string;
    resolved: boolean;
    timestamp: number;
    timezone: string;
  };
}

export interface UliItemTruckStatuses {
  truckId: number;
  truckSize: TruckSizeUnion;
  statuses: UliItemStatus[];
}

export interface UliItemLoadNumber {
  huNumber: number;
  loadNumber: string;
  region: string;
}

export interface UliItemMainInfo {
  customsGoods: boolean;
  dangerousGoods: boolean;
  documentNumbers: string[];
  grossWeight: number;
  numberOfHU: number;
  numberOfLoadmeters: number;
}

export interface TransportOrderNumberModel {
  partyAddress: AddressModel;
  toNumber: string;
  handlingUnits: number;
  totalGrossWeight: number;
  totalVolume: number;
}

export class UliItemUliInfo {
  customsGoods: boolean;
  confirmationPending: boolean;
  dangerousGoods: boolean;
  delay: DeviationType;
  documentNumbers: string[];
  equipmentTypes: string[];
  grossWeight: number;
  leadUli: boolean;
  loadNumber: UliItemLoadNumber;
  numberOfHU: number;
  numberOfLoadmeters: number;
  origin: UliOrigin;
  registrationId: number;
  scheduledRegistrationTime: LocalDate;
  slotId: number;
  toNumbers: TransportOrderNumberModel[];
  type: UliActivityType;
  uliCollectionCount: number;
  uliNumber: string;
  allocationDelay: AllocationDurationType;
  allocationDelayMinutes: number;
  registrationDelayMinutes: number;

  constructor(params?: UliItemUliInfo) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class UliItemOverviewModel {
  timeSlotInfo: UliItemAllocation;
  truckInfos: UliItemTruck[];
  uliInfo: UliItemUliInfo;
  uliStatus: UliStatus;
  truckStatus: TruckStatus;

  constructor(params?: UliItemOverviewModel) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class UliItemUliInfoForm {
  constructor(
    model: UliItemUliInfo,
    uliStatus: UliStatus,
    public grossWeight: ControlProperty<number> = [
      {
        value: model.grossWeight,
        disabled:
          model?.origin === UliOrigin.EdiManager ||
          model?.origin === UliOrigin.TMSEurolog ||
          model?.origin === UliOrigin.Erp ||
          uliStatus !== UliStatus.Init,
      },
      [Validators.pattern(FRACTIONAL_NUMBER_PATTERN)],
    ],
    public customsGoods: ControlProperty<boolean> = [
      { value: model.customsGoods, disabled: uliStatus !== UliStatus.Init },
      null,
    ],
    public dangerousGoods: ControlProperty<boolean> = [
      { value: model.dangerousGoods, disabled: uliStatus !== UliStatus.Init },
      null,
    ],
    public documentNumbers: ControlProperty<string[]> = [
      {
        value: model.documentNumbers,
        disabled:
          model?.origin === UliOrigin.EdiManager ||
          model?.origin === UliOrigin.TMSEurolog ||
          model?.origin === UliOrigin.Erp ||
          uliStatus !== UliStatus.Init,
      },
      [],
    ],
    public numberOfHU: ControlProperty<number> = [
      {
        value: model.numberOfHU,
        disabled:
          model?.origin === UliOrigin.EdiManager ||
          model?.origin === UliOrigin.TMSEurolog ||
          model?.origin === UliOrigin.Erp ||
          uliStatus !== UliStatus.Init,
      },
      [Validators.pattern(NATURAL_NUMBER_PATTERN)],
    ],
    public numberOfLoadmeters: ControlProperty<number> = [
      {
        value: model.numberOfLoadmeters,
        disabled:
          model?.origin === UliOrigin.EdiManager || model?.origin === UliOrigin.Erp || uliStatus !== UliStatus.Init,
      },
      [Validators.pattern(FRACTIONAL_NUMBER_PATTERN)],
    ],
  ) {}
}

export class UliItemOverviewForm {
  constructor(
    model: UliItemOverviewModel,
    fb: UntypedFormBuilder,
    uliStatus: UliStatus,
    public uliInfo: UntypedFormGroup = fb.group(new UliItemUliInfoForm(model.uliInfo, uliStatus)),
  ) {}
}
